import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { featureRequestsApi } from '../lib/api';
import { 
  Lightbulb, 
  ThumbsUp, 
  Send, 
  Loader2,
  CheckCircle,
  Clock,
  Rocket,
  XCircle,
  AlertCircle
} from 'lucide-react';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'subscriptions', label: 'Subscription Management' },
  { value: 'scanning', label: 'Email/Bank Scanning' },
  { value: 'alerts', label: 'Alerts & Notifications' },
  { value: 'cancellation', label: 'Cancellation Help' },
  { value: 'billing', label: 'Billing & Plans' },
  { value: 'ui', label: 'User Interface' },
  { value: 'integrations', label: 'Integrations' },
  { value: 'other', label: 'Other' },
];

const STATUS_CONFIG = {
  pending: { label: 'Under Review', color: 'bg-gray-500', icon: Clock },
  planned: { label: 'Planned', color: 'bg-blue-500', icon: Rocket },
  in_progress: { label: 'In Progress', color: 'bg-amber-500', icon: Loader2 },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  declined: { label: 'Declined', color: 'bg-red-500', icon: XCircle },
};

const FeatureRequestsPage = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [votingId, setVotingId] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('general');

  const isDemo = user?.is_demo || user?.email === 'demo@subguardhq.com';

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const response = await featureRequestsApi.getAll();
      setRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to load feature requests:', error);
      toast.error('Failed to load feature requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDemo) {
      toast.error('Demo accounts cannot submit feature requests. Please sign up for a free account!');
      return;
    }
    if (!title.trim() || !description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await featureRequestsApi.create(title, description, category);
      toast.success('Feature request submitted! Thank you for your feedback.');
      setTitle('');
      setDescription('');
      setCategory('general');
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (requestId) => {
    if (isDemo) {
      toast.error('Demo accounts cannot vote. Please sign up for a free account!');
      return;
    }
    setVotingId(requestId);
    try {
      const response = await featureRequestsApi.vote(requestId);
      setRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, votes: response.data.votes, user_has_voted: response.data.user_has_voted }
          : req
      ));
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Failed to vote');
    } finally {
      setVotingId(null);
    }
  };

  return (
    <DashboardLayout>
    <div className="space-y-6" data-testid="feature-requests-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
          Feature Requests
        </h1>
        <p className="text-gray-600 mt-1">
          Help shape the future of SubGuard! Submit ideas or vote on what matters most to you.
        </p>
      </div>

      {/* Demo Account Warning */}
      {isDemo && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Demo Account Limitations</p>
              <p className="text-sm text-amber-700 mt-1">
                You're using a demo account. Submitting requests and voting are disabled. 
                Sign up for a free account to participate!
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Submit Form */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="w-5 h-5 text-[#064E3B]" />
              Submit an Idea
            </CardTitle>
            <CardDescription>
              Have a feature in mind? Tell us about it!
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">What would you like to see?</label>
                <Input
                  placeholder="e.g., Add support for bank linking"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                  disabled={isDemo}
                  data-testid="feature-title-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Select value={category} onValueChange={setCategory} disabled={isDemo}>
                  <SelectTrigger data-testid="feature-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Tell us more</label>
                <Textarea
                  placeholder="Describe your feature request in detail..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  maxLength={1000}
                  disabled={isDemo}
                  data-testid="feature-description-input"
                />
                <p className="text-xs text-gray-400 text-right">{description.length}/1000</p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#064E3B] hover:bg-[#043927]"
                disabled={isSubmitting || isDemo || !title.trim() || !description.trim()}
                data-testid="submit-feature-btn"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Submit Request
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Voting List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Community Requests
            </h2>
            <Badge variant="outline" className="text-[#064E3B] border-[#064E3B]">
              {requests.length} requests
            </Badge>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : requests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <Lightbulb className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No feature requests yet.</p>
                <p className="text-sm">Be the first to submit an idea!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => {
                const StatusIcon = STATUS_CONFIG[request.status]?.icon || Clock;
                return (
                  <Card 
                    key={request.id} 
                    className={`transition-all hover:shadow-md ${request.is_own_request ? 'border-[#064E3B]/30 bg-[#F0FDF4]/50' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Vote Button */}
                        <div className="flex flex-col items-center">
                          <Button
                            variant={request.user_has_voted ? "default" : "outline"}
                            size="sm"
                            className={`h-14 w-14 p-0 ${
                              request.user_has_voted 
                                ? 'bg-[#064E3B] hover:bg-[#043927]' 
                                : 'hover:border-[#064E3B] hover:text-[#064E3B]'
                            } ${isDemo ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() => handleVote(request.id)}
                            disabled={votingId === request.id || isDemo}
                            data-testid={`vote-btn-${request.id}`}
                          >
                            {votingId === request.id ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                              <ThumbsUp className="w-5 h-5" />
                            )}
                          </Button>
                          <span className="text-sm font-semibold mt-1">{request.votes}</span>
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-medium text-gray-900">{request.title}</h4>
                            <Badge 
                              variant="secondary" 
                              className={`${STATUS_CONFIG[request.status]?.color} text-white text-xs shrink-0`}
                            >
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {STATUS_CONFIG[request.status]?.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{request.description}</p>
                          <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                            <Badge variant="outline" className="text-xs">
                              {CATEGORIES.find(c => c.value === request.category)?.label || request.category}
                            </Badge>
                            <span>{new Date(request.created_at).toLocaleDateString()}</span>
                            {request.is_own_request && (
                              <span className="text-[#064E3B] font-medium">Your request</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    </DashboardLayout>
  );
};

export default FeatureRequestsPage;
